import { graphql, useFragment } from "react-relay";
import {
  ActorAvatar,
  AHIcon,
  AHIconType,
  AHText,
  Box,
  BoxProps,
  Card,
  Circle,
  ColorType,
  Overlap,
  useCircleSize,
  useDialogButtonHeight,
} from "arkh";
import {
  TodoContact_todo$key,
  TodoFeedbackStatus,
} from "./__generated__/TodoContact_todo.graphql";
import { Platform } from "react-native";
import { FormattedMessage } from "react-intl";

export function TodoContact(props: {
  todo: TodoContact_todo$key;
  boxProps?: BoxProps;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoContact_todo on Todo {
        contact {
          name
          email
          phoneNumber
          address
        }
        feedbackStatus
        allTodoFeedbacks {
          createdByActor {
            ...Avatar_actor
          }
        }
      }
    `,
    props.todo
  );

  const allTodoFeedbacksActors = (todo.allTodoFeedbacks ?? []).flatMap((s) =>
    s?.createdByActor ? [s?.createdByActor] : []
  );
  const avatarSize = useCircleSize("smallMedium");
  if (!todo.contact) return null;

  return (
    <Box {...props.boxProps}>
      <Card defaultBorder>
        <Box
          spaceBottom={todo.feedbackStatus ? "none" : "medium"}
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <AHText type="detail" color="darkerText">
            <FormattedMessage
              defaultMessage="Contact"
              description="Todo contact title"
            />
          </AHText>

          {todo.feedbackStatus ? (
            <FeedbackStatusItem
              avatar={
                <>
                  {todo.feedbackStatus ? (
                    <Overlap
                      data={allTodoFeedbacksActors}
                      maxNumberOfItems={5}
                      render={(actor) => (
                        <ActorAvatar size="small" actor={actor} />
                      )}
                      viewWidth={avatarSize}
                    />
                  ) : null}
                </>
              }
              feedbackStatus={todo.feedbackStatus}
              ctaMode
            />
          ) : null}
        </Box>
        <Box
          gap="medium"
          style={Platform.OS === "web" ? { flexDirection: "row" } : {}}
        >
          <Item icon={"user"}>{todo.contact.name}</Item>
          <Item icon={"email"}>{todo.contact.email}</Item>
          <Item icon={"phone"}>{todo.contact.phoneNumber}</Item>
        </Box>
      </Card>
    </Box>
  );
}

function FeedbackStatusItemContainer(props: {
  children: React.ReactNode;
  borderColor?: ColorType;
  ctaMode?: boolean;
  avatar?: React.ReactNode;
}) {
  const height = useDialogButtonHeight();
  return (
    <Box
      borderColor={props.borderColor ?? "border-primary"}
      cornerRadius="large"
      style={{
        borderWidth: 1,
        flexDirection: "row",
        alignItems: "center",

        height,
        overflow: "hidden",
      }}
      gap="small"
      spaceHorizontal="small"
      spaceLeft={props.avatar ? "xsmall" : "small"}
      spaceRight={props.ctaMode ? "xsmall" : "small"}
    >
      {props.avatar}
      {props.children}
    </Box>
  );
}
export function FeedbackStatusItem(props: {
  feedbackStatus: TodoFeedbackStatus;
  right?: React.ReactNode;
  left?: React.ReactNode;
  ctaMode?: boolean;
  avatar?: React.ReactNode;
}) {
  const right = props.right ?? null;
  const left = props.left ?? null;
  switch (props.feedbackStatus) {
    case "FEEDBACK_DISMISSED":
      return (
        <FeedbackStatusItemContainer>
          {left}
          <AHText type="subtitle" color={"text-secondary"}>
            <TodoFeedbackStatusText feedbackStatus="FEEDBACK_DISMISSED" />
          </AHText>
          <Circle size="badge" color={"status-inactive"} />
          {right}
        </FeedbackStatusItemContainer>
      );
    case "FEEDBACK_COMPLETED":
      return (
        <FeedbackStatusItemContainer avatar={props.avatar}>
          {left}
          <AHText type="subtitle" color={"text-secondary"}>
            <TodoFeedbackStatusText feedbackStatus="FEEDBACK_COMPLETED" />
          </AHText>
          <Circle size="badge" color={"status-positive"} />
          {right}
        </FeedbackStatusItemContainer>
      );
    case "FEEDBACK_NEEDED":
      return (
        <FeedbackStatusItemContainer
          ctaMode={props.ctaMode}
          borderColor={props.ctaMode ? "status-feedback-negative" : undefined}
        >
          {left}
          <AHText type="subtitle" color={"status-feedback-negative"}>
            <TodoFeedbackStatusText feedbackStatus="FEEDBACK_NEEDED" />
          </AHText>
          {props.ctaMode ? (
            <Circle size="smallMedium" color={"status-feedback-negative"}>
              <AHIcon type="arrow-right" size="small" color="text-inverted" />
            </Circle>
          ) : (
            <Circle size="badge" color={"status-feedback-negative"} />
          )}
          {right}
          {props.ctaMode ? (
            <Box
              color="status-feedback-negative"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                opacity: 0.1,
              }}
            />
          ) : null}
        </FeedbackStatusItemContainer>
      );
    default:
      return null;
  }
}

export function TodoFeedbackStatusText({
  feedbackStatus,
}: {
  feedbackStatus: TodoFeedbackStatus;
}) {
  switch (feedbackStatus) {
    case "FEEDBACK_COMPLETED":
      return <FormattedMessage defaultMessage="Replied " />;
    case "FEEDBACK_DISMISSED":
      return <FormattedMessage defaultMessage="No reply needed" />;
    case "FEEDBACK_NEEDED":
      return <FormattedMessage defaultMessage="Awaiting reply" />;
    default:
      return null;
  }
}

function Item(props: { children?: React.ReactNode; icon: AHIconType }) {
  if (!props.children) return null;
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }} gap="small">
      <AHIcon color="darkText" type={props.icon} />
      <AHText type="detail" color="darkerText">
        {props.children}
      </AHText>
    </Box>
  );
}
